import { APIResponse } from "../../../models/NetworkResponse";
import { getAPI, postAPI, putAPI } from "../../../api/Networking";
import {
  Amenity,
  EventType,
  PropertyCategory,
  PropertyType,
} from "../../../models/Property/Property";
import { GetListParams } from "../../../models/Property/PropertyParams";
import { PropertyFilters } from "../../../models/Property/PropertyFilters";

/** Normalize API result: either direct array or { list: [] } */
function toList<T>(result: T[] | { list?: T[] } | null | undefined): T[] {
  if (Array.isArray(result)) return result;
  if (result && typeof result === "object" && "list" in result)
    return (result as { list: T[] }).list || [];
  return [];
}

export default class PropertyAPI {
  getEventTypes = async (
    params: GetListParams = {}
  ): Promise<EventType[]> => {
    const queryParams = new URLSearchParams();
    if (params.page_no != null) queryParams.append("page_no", String(params.page_no));
    if (params.limit != null) queryParams.append("limit", String(params.limit));
    queryParams.append("active", params.active === false ? "false" : "true");

    const response = await getAPI(
      `/v5/event-management/event-types?${queryParams.toString()}`
    );
    return toList(response?.result);
  };

  getPropertyCategories = async (
    params: GetListParams = {}
  ): Promise<PropertyCategory[]> => {
    const queryParams = new URLSearchParams();
    if (params.page_no != null) queryParams.append("page_no", String(params.page_no));
    if (params.limit != null) queryParams.append("limit", String(params.limit));
    queryParams.append("active", params.active === false ? "false" : "true");

    const response = await getAPI(
      `/v5/event-management/property-categories?${queryParams.toString()}`
    );
    return toList(response?.result);
  };

  getPropertyTypes = async (
    params: GetListParams = {}
  ): Promise<PropertyType[]> => {
    const queryParams = new URLSearchParams();
    if (params.page_no != null) queryParams.append("page_no", String(params.page_no));
    if (params.limit != null) queryParams.append("limit", String(params.limit));
    queryParams.append("active", params.active === false ? "false" : "true");

    const response = await getAPI(
      `/v5/event-management/property-types?${queryParams.toString()}`
    );
    return toList(response?.result);
  };

  getAmenities = async (
    params: GetListParams & { search_text?: string } = {}
  ): Promise<Amenity[]> => {
    const queryParams = new URLSearchParams();
    if (params.page_no != null) queryParams.append("page_no", String(params.page_no));
    if (params.limit != null) queryParams.append("limit", String(params.limit));
    if (params.search_text) queryParams.append("search_text", params.search_text);
    queryParams.append("active", params.active === false ? "false" : "true");

    const response = await getAPI(
      `/v5/event-management/amenities?${queryParams.toString()}`
    );
    return toList(response?.result);
  };

  createAmenity = async (name: string): Promise<APIResponse & { result?: Amenity }> => {
    const response = await postAPI(`/v5/event-management/amenities/create`, { name });
    return response;
  };

  /** Backend expects JSON body (AdminPropertyCreateSchema), not FormData */
  createProperty = async (body: Record<string, unknown>): Promise<APIResponse> => {
    const response = await postAPI(
      `/v5/event-management/properties/create`,
      body
    );
    return response;
  };

  /** GET list - query: page, limit, search_text */
  getProperties = async (filters: PropertyFilters = {}): Promise<APIResponse> => {
    const queryParams = new URLSearchParams();
    if (filters.page != null) queryParams.append("page", String(filters.page));
    if (filters.limit != null) queryParams.append("limit", String(filters.limit));
    if (filters.search_text) queryParams.append("search_text", filters.search_text);
    const response = await getAPI(
      `/v5/event-management?${queryParams.toString()}`
    );
    return response;
  };

  getPropertyById = async (id: number): Promise<APIResponse> => {
    const response = await getAPI(
      `/v5/event-management/${id}`
    );
    return response;
  };

  /** PUT /properties/:id - same body as create (AdminPropertyCreateSchema) */
  updateProperty = async (
    id: number,
    body: Record<string, unknown>
  ): Promise<APIResponse> => {
    const response = await putAPI(
      `/v5/event-management/${id}`,
      body
    );
    return response;
  };
}
